import { Avatar, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { Text } from "@/components/podkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import { useListMembersInfinite, type PlainOrganizationMember } from "@/queries/organization-queries";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";
import { SEARCH_DEBOUNCE_MS } from "@/utils/debounce";

type UserSelectorProps = {
    /** IDs of users to exclude from the selectable list (e.g. users who already have access) */
    excludeUserIds: Set<string>;
    onAdd: (userIds: string[]) => void;
    className?: string;
};

export const UserSelector: FC<UserSelectorProps> = ({ excludeUserIds, onAdd, className }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS, { trailing: true });

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isPending, isFetching } = useListMembersInfinite({
        search: debouncedSearchQuery.trim(),
        enabled: isOpen,
    });

    // Reset selections and search when dropdown closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedIds(new Set());
            setSearchQuery("");
        }
    }, [isOpen]);

    const members = useMemo(() => {
        if (!data) return [];
        return data.pages.flatMap((page) => page.members);
    }, [data]);

    // Filter out excluded users (those who already have access)
    const filteredUsers = useMemo(() => {
        return members.filter((m) => !excludeUserIds.has(m.userId));
    }, [members, excludeUserIds]);

    const handleToggleSelection = useCallback((userId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    }, []);

    const handleConfirm = useCallback(() => {
        if (selectedIds.size > 0) {
            onAdd(Array.from(selectedIds));
            setIsOpen(false);
        }
    }, [selectedIds, onAdd]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && selectedIds.size > 0) {
                e.preventDefault();
                handleConfirm();
            }
        },
        [selectedIds.size, handleConfirm],
    );

    const handleScrollEnd = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
            <PopoverTrigger asChild>
                <button
                    className={cn(
                        "flex w-full items-center justify-between",
                        "rounded-lg border border-border-light bg-surface-pure px-4 py-[7px]",
                        "text-base text-content-tertiary",
                        "hover:border-border-base",
                        "focus-visible:ring-ring focus:border-border-brand focus:bg-surface-button-clear-accent focus:outline-none focus-visible:ring-1",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                        className,
                    )}
                    data-testid="user-selector-trigger"
                >
                    <span>Select a user to add</span>
                    <ChevronDown className="size-4 text-content-primary" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] p-0"
                onKeyDown={handleKeyDown}
            >
                {/* Search bar with Share button */}
                <div className="flex items-center gap-2 p-2 pb-1">
                    <SearchInput
                        wrapperClassName="flex-1"
                        placeholder="Search by name or email"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery("")}
                        isLoading={isFetching || isFetchingNextPage}
                        data-testid="user-selector-search"
                        data-tracking-id="user-selector-search"
                    />
                    <Button
                        onClick={handleConfirm}
                        variant="primary"
                        disabled={selectedIds.size === 0}
                        data-testid="user-selector-share-button"
                        data-tracking-id="user-selector-share"
                    >
                        Share
                    </Button>
                </div>

                {/* Users list with infinite scroll */}
                <ScrollArea
                    orientation="vertical"
                    className="max-h-80"
                    onScrollEnd={handleScrollEnd}
                    scrollEndThreshold={200}
                >
                    {isPending ? (
                        <div className="px-4 py-8 text-center text-sm text-content-secondary">Loading members...</div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-content-secondary">
                            {searchQuery.trim() ? "No users found matching your search" : "No users available"}
                        </div>
                    ) : (
                        <div>
                            {filteredUsers.map((user) => (
                                <UserRow
                                    key={user.userId}
                                    user={user}
                                    isSelected={selectedIds.has(user.userId)}
                                    onToggle={handleToggleSelection}
                                />
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};

const UserRow: FC<{
    user: PlainOrganizationMember;
    isSelected: boolean;
    onToggle: (userId: string) => void;
}> = ({ user, isSelected, onToggle }) => {
    return (
        <div
            className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-surface-02"
            onClick={() => onToggle(user.userId)}
            data-testid={`user-selector-item-${user.userId}`}
            data-tracking-id="user-selector-item"
        >
            <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggle(user.userId)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`user-selector-checkbox-${user.userId}`}
                data-tracking-id="user-selector-checkbox"
            />
            <Avatar size={32} className="size-8 rounded-full">
                <AvatarImage src={user.avatarUrl} alt={user.fullName} />
                <AvatarInitials name={user.fullName} size={32} />
            </Avatar>
            <div className="flex min-w-0 flex-1 flex-col">
                <Text className="truncate text-base font-medium">{user.fullName}</Text>
                <Text className="truncate text-sm text-content-tertiary">{user.email}</Text>
            </div>
        </div>
    );
};
